import React, { useMemo } from 'react';
import {
  format,
  parseISO,
  isToday,
  isTomorrow,
  differenceInCalendarDays,
  startOfDay,
} from 'date-fns';
import {
  CalendarDays,
  Clock,
  MapPin,
  IndianRupee,
  Camera,
  AlertCircle,
  Users,
  Star,
  Tag,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { EVENT_TYPE_CONFIG } from './eventConstants';

const TYPE_ICONS = {
  shoot_day: Camera,
  deadline: AlertCircle,
  review: Star,
  meeting: Users,
  custom: Tag,
};

/**
 * AgendaView — chronological event list grouped by date.
 */
export default function AgendaView({ events, currentMonth, onEventClick, isReadOnly }) {
  /* ── Group events by date ─────────────────────────────────────── */
  const grouped = useMemo(() => {
    const map = new Map();
    const sorted = [...events].sort(
      (a, b) => new Date(a.start_at) - new Date(b.start_at)
    );
    for (const ev of sorted) {
      const d = format(
        typeof ev.start_at === 'string' ? parseISO(ev.start_at) : new Date(ev.start_at),
        'yyyy-MM-dd'
      );
      if (!map.has(d)) map.set(d, []);
      map.get(d).push(ev);
    }
    return map;
  }, [events]);

  if (events.length === 0) {
    return (
      <div className="rounded-xl border bg-card p-12 text-center">
        <CalendarDays className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
        <p className="font-display text-xl font-semibold">No events this month</p>
        <p className="text-sm text-muted-foreground mt-1">
          {isReadOnly
            ? 'Events created at the film level will appear here.'
            : 'Click "New Event" to schedule your first production event.'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {[...grouped.entries()].map(([dateKey, dayEvents]) => {
        const date = parseISO(dateKey);
        const today = isToday(date);
        const tomorrow = isTomorrow(date);
        const daysAway = differenceInCalendarDays(date, startOfDay(new Date()));

        return (
          <div key={dateKey}>
            {/* Date header */}
            <div className="flex items-center gap-3 mb-3">
              <div
                className={cn(
                  'flex flex-col items-center justify-center rounded-xl w-14 h-14 border',
                  today
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-card border-border'
                )}
              >
                <span className="text-[10px] font-bold uppercase tracking-wider leading-none">
                  {format(date, 'EEE')}
                </span>
                <span className="text-xl font-bold leading-none mt-0.5">
                  {format(date, 'd')}
                </span>
              </div>
              <div>
                <p className="font-semibold text-sm">
                  {today
                    ? 'Today'
                    : tomorrow
                      ? 'Tomorrow'
                      : format(date, 'EEEE, MMMM d')}
                </p>
                <p className="text-xs text-muted-foreground">
                  {dayEvents.length} event{dayEvents.length !== 1 ? 's' : ''}
                  {daysAway > 1 && ` · in ${daysAway} days`}
                  {daysAway < 0 && ` · ${Math.abs(daysAway)} days ago`}
                </p>
              </div>
            </div>

            {/* Event cards */}
            <div className="space-y-2 ml-[68px]">
              {dayEvents.map((ev) => {
                const cfg = EVENT_TYPE_CONFIG[ev.event_type] || EVENT_TYPE_CONFIG.custom;
                const TypeIcon = TYPE_ICONS[ev.event_type] || Tag;
                const startDate = typeof ev.start_at === 'string' ? parseISO(ev.start_at) : new Date(ev.start_at);
                const endDate = typeof ev.end_at === 'string' ? parseISO(ev.end_at) : new Date(ev.end_at);

                return (
                  <button
                    key={ev.id}
                    onClick={() => onEventClick(ev)}
                    className={cn(
                      'w-full text-left rounded-xl border bg-card p-4 transition-all',
                      'hover:ring-1 hover:ring-primary/30 hover:shadow-sm',
                      'flex gap-4 items-start group'
                    )}
                  >
                    {/* Left colour stripe + icon */}
                    <div
                      className={cn(
                        'flex items-center justify-center rounded-lg h-10 w-10 shrink-0',
                        cfg.light
                      )}
                    >
                      <TypeIcon className="h-5 w-5" />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span
                          className={cn(
                            'text-[10px] font-bold uppercase tracking-wider',
                            cfg.text
                          )}
                        >
                          {cfg.label}
                        </span>
                      </div>
                      <p className="font-semibold text-sm truncate group-hover:text-primary transition-colors">
                        {ev.title}
                      </p>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {ev.all_day
                            ? 'All Day'
                            : `${format(startDate, 'h:mm a')} – ${format(endDate, 'h:mm a')}`}
                        </span>
                        {ev.location_text && (
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {ev.location_text}
                          </span>
                        )}
                        {ev.cost_amount != null && Number(ev.cost_amount) > 0 && (
                          <span className="flex items-center gap-1">
                            <IndianRupee className="h-3 w-3" />
                            {Number(ev.cost_amount).toLocaleString('en-IN')}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
